<template>
    <div class="tile-content tile-content-secondary">
        <p class="stack-m">Webhooks allow a way for third party applications to receive information about certain events
            within your
            Rebilly account. You may create multiple webhooks. To use a webhook, you need to have an URL in order to
            receive the event information.
        </p>
        <webhook-endpoint-form
            ref="webhook-endpoint-form"
            :is-form-updating="isFormUpdating"
            :is-loading="isLoading"
            :webhook="action"
            :webhook-credential="webhookCredential"
            v-on:input="onInput"
            v-on:test="testWebhook"/>
    </div>
</template>

<script>
    import {vCheckbox, vIcon} from '@/components/form';
    import {minValue, numeric} from 'vuelidate/lib/validators';
    import WebhookEndpointForm from '@/components/webhook/webhook-endpoint-form';
    import WebhookCredentialModel from '@/common/models/webhook-credential-model';
    import TriggerWebhookActionModel from '@/common/models/rules/actions/trigger-webhook-action-model';
    import BaseAction from '../base-action';

    export default {
        name: 'trigger-webhook',
        extends: BaseAction,
        components: {
            vIcon, vCheckbox, WebhookEndpointForm,
        },
        data() {
            return {
                isLoading: false,
                webhookCredential: new WebhookCredentialModel(),
            };
        },
        validations: {
            action: {
                ttl: {
                    numeric,
                    minValue: minValue(30),
                },
            },
        },
        methods: {
            getValidationAggregate() {
                return {
                    name: 'trigger-webhook',
                    rules: [
                        {
                            name: 'Trigger webhook fields are invalid.',
                            delegate: () => {
                                const form = this.$refs['webhook-endpoint-form'];
                                form.$v.$touch();
                                return form.$v.webhook.$error || form.$v.webhookCredential.$error;
                            },
                        },
                    ],
                };
            },
            onInput({key, value}) {
                const [modelName, ...keysList] = key.split('.');
                const keys = keysList.reverse();
                let data = modelName === 'webhook' ? this.action : this.webhookCredential;

                while (keys.length) {
                    const k = keys.pop();
                    if (keys.length === 0) {
                        Reflect.set(data, k, value);
                    } else {
                        data = data[k];
                    }
                }
            },
            async asyncSaveStep() {
                if (this.isFormUpdating && this.$refs['webhook-endpoint-form'].useCredentialHash) {
                    return true;
                }
                this.webhookCredential.parseHostFromUrl(this.action.url);
                const params = {
                    ...this.webhookCredential.getPayload(),
                };
                const credentialsPayload = await this.$api(
                    ({credentialHashes}) => credentialHashes.createWebhookCredential({data: params}),
                );
                this.action.credentialHash = credentialsPayload.fields.hash;
                return true;
            },
            async testWebhook({url, body}) {
                this.isLoading = true;
                const {eventType} = this.$route.params;

                try {
                    const testWebhook = new TriggerWebhookActionModel({
                        ...this.action.getPayload(), ...{url, body},
                    });
                    // include host from URL
                    this.webhookCredential.parseHostFromUrl(testWebhook.url);
                    const paramsWebhookCredential = {
                        data: this.webhookCredential.getPayload(),
                    };
                    const {fields} = await this.$api(({credentialHashes}) => credentialHashes.createWebhookCredential(paramsWebhookCredential));
                    testWebhook.credentialHash = fields.hash;
                    await this.$api(({previews}) => previews.triggerWebhookRuleAction({
                        data: {
                            ...testWebhook.getPayload(),
                            eventType,
                        },
                    }));
                    this.$toast.positive(`Webhook successfully tested`);
                } catch (error) {
                    this.serverError = error;
                    this.$toast.negative('Test failed, please confirm your webhook credentials and try again.');
                } finally {
                    this.isLoading = false;
                }
            },
        },
    };
</script>
